import React, { useState } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    Card,
    Grid,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";
import { db } from "../../firebase"; // Adjust path as per your folder structure
import {
    collection,
    getDocs,
    query,
    where,
    Timestamp,
} from "firebase/firestore";

function Dailyledger() {
    const today = new Date().toISOString().split("T")[0];

    const [selectedDate, setSelectedDate] = useState(today);
    const [loading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);

    const [credits, setCredits] = useState([]);
    const [debits, setDebits] = useState([]);
    const [payModeSummary, setPayModeSummary] = useState({});

    // Fixed Base Configuration
    const BASE_DATE = "2026-07-24";
    const BASE_OPENING_BALANCE = 13073;

    const [openingBalance, setOpeningBalance] = useState(BASE_OPENING_BALANCE);

    // Helper: Normalize cash payment mode strings
    const isCashMode = (mode = "") => {
        const m = mode.trim().toLowerCase();
        return m === "cash" || m === "by cash";
    };

    // Helper: Sum up Cash Income, Non-Cash Income, and Expenses (including Pigmi & RD Savings, excluding monthly salary)
    const getTotalsBetweenDates = async (startDateStr, endDateStr) => {
        const start = Timestamp.fromDate(new Date(`${startDateStr}T00:00:00`));
        const end = Timestamp.fromDate(new Date(`${endDateStr}T23:59:59`));

        const qPay = query(
            collection(db, "customer_history"),
            where("date", ">=", start),
            where("date", "<=", end)
        );
        const qTrips = query(
            collection(db, "trips"),
            where("grossDate", ">=", start),
            where("grossDate", "<=", end)
        );
        const qOtherIncome = query(
            collection(db, "otherIncome"),
            where("date", ">=", start),
            where("date", "<=", end)
        );
        const qExpense = query(
            collection(db, "otherExpenses"),
            where("date", ">=", start),
            where("date", "<=", end)
        );
        const qDealerPay = query(
            collection(db, "dealerPayments"),
            where("date", ">=", start),
            where("date", "<=", end)
        );
        const qStaffAdvance = query(
            collection(db, "staffadvances"),
            where("date", ">=", startDateStr),
            where("date", "<=", endDateStr)
        );
        const qPigmi = query(
            collection(db, "pigmi_savings"),
            where("depositDate", ">=", startDateStr),
            where("depositDate", "<=", endDateStr)
        );
        const qRdSavings = query(
            collection(db, "rd_savings"),
            where("depositDate", ">=", startDateStr),
            where("depositDate", "<=", endDateStr)
        );

        const [
            paySnap, 
            tripSnap, 
            otherIncomeSnap, 
            expenseSnap, 
            dealerPaySnap, 
            staffAdvSnap,
            pigmiSnap,
            rdSnap
        ] = await Promise.all([
            getDocs(qPay),
            getDocs(qTrips),
            getDocs(qOtherIncome),
            getDocs(qExpense),
            getDocs(qDealerPay),
            getDocs(qStaffAdvance),
            getDocs(qPigmi),
            getDocs(qRdSavings),
        ]);

        let totalIncome = 0;
        let totalNonCashIncome = 0;
        let totalExpenses = 0;

        // Customer collections
        paySnap.docs.forEach((doc) => {
            const d = doc.data();
            if (!d.tripId) {
                const amt = Number(d.amount || d.paidAmount || 0);
                const mode = d.paymentMode || d.payMode || "Cash";
                totalIncome += amt;
                if (!isCashMode(mode)) {
                    totalNonCashIncome += amt;
                }
            }
        });

        // Trip income
        tripSnap.docs.forEach((doc) => {
            const trip = doc.data();
            (trip.payments || []).forEach((p) => {
                const amt = Number(p.amount || 0);
                const mode = p.mode || p.paymentMode || "Cash";
                totalIncome += amt;
                if (!isCashMode(mode)) {
                    totalNonCashIncome += amt;
                }
            });
        });

        // Other income
        otherIncomeSnap.docs.forEach((doc) => {
            const d = doc.data();
            const amt = Number(d.amount || 0);
            const mode = d.paymentMode || d.mode || "Cash";
            totalIncome += amt;
            if (!isCashMode(mode)) {
                totalNonCashIncome += amt;
            }
        });

        // General expenses
        expenseSnap.docs.forEach((doc) => {
            totalExpenses += Number(doc.data().amount || 0);
        });

        // Dealer payments
        dealerPaySnap.docs.forEach((doc) => {
            totalExpenses += Number(doc.data().amount || 0);
        });

        // Staff advance payments
        staffAdvSnap.docs.forEach((doc) => {
            totalExpenses += Number(doc.data().payment || 0);
        });

        // Pigmi / Savings deposits
        pigmiSnap.docs.forEach((doc) => {
            totalExpenses += Number(doc.data().amount || 0);
        });

        // RD Savings deposits
        rdSnap.docs.forEach((doc) => {
            totalExpenses += Number(doc.data().amount || 0);
        });

        return { totalIncome, totalNonCashIncome, totalExpenses };
    };

    // Main Fetch Function
    const fetchLedgerData = async () => {
        if (!selectedDate) {
            alert("Please select a date.");
            return;
        }

        setLoading(true);
        try {
            // --- Step 1: Compute Dynamic Opening Balance ---
            let computedOpeningBalance = BASE_OPENING_BALANCE;

            if (selectedDate > BASE_DATE) {
                const selDateObj = new Date(selectedDate);
                const prevDayObj = new Date(selDateObj);
                prevDayObj.setDate(prevDayObj.getDate() - 1);
                const prevDayStr = prevDayObj.toISOString().split("T")[0];

                const accumulated = await getTotalsBetweenDates(BASE_DATE, prevDayStr);

                computedOpeningBalance =
                    BASE_OPENING_BALANCE +
                    accumulated.totalIncome -
                    accumulated.totalNonCashIncome -
                    accumulated.totalExpenses;
            } else if (selectedDate < BASE_DATE) {
                computedOpeningBalance = BASE_OPENING_BALANCE;
            }

            setOpeningBalance(computedOpeningBalance);

            // --- Step 2: Fetch Master Data ---
            const [customersSnap, dealerSnap, staffSnap, incomeHeadsSnap] = await Promise.all([
                getDocs(collection(db, "customers")),
                getDocs(collection(db, "dealers")),
                getDocs(collection(db, "staff")),
                getDocs(collection(db, "incomeHeads")),
            ]);

            const customerMap = {};
            customersSnap.forEach((doc) => {
                const data = doc.data();
                customerMap[doc.id] = data.customerName || data.name || "Customer";
            });

            const dealerMap = {};
            dealerSnap.forEach((doc) => {
                dealerMap[doc.id] = doc.data().dealerName || "Unknown Dealer";
            });

            const staffMap = {};
            staffSnap.forEach((doc) => {
                staffMap[doc.id] = doc.data().name || "Unknown Staff";
            });

            const incomeHeadMap = {};
            incomeHeadsSnap.forEach((doc) => {
                incomeHeadMap[doc.id] = doc.data().name || "Unknown Income Head";
            });

            // --- Step 3: Fetch Current Day Transactions ---
            const currentStart = Timestamp.fromDate(new Date(`${selectedDate}T00:00:00`));
            const currentEnd = Timestamp.fromDate(new Date(`${selectedDate}T23:59:59`));

            const qPayCurr = query(
                collection(db, "customer_history"),
                where("date", ">=", currentStart),
                where("date", "<=", currentEnd)
            );
            const qTripsCurr = query(
                collection(db, "trips"),
                where("grossDate", ">=", currentStart),
                where("grossDate", "<=", currentEnd)
            );
            const qOtherIncomeCurr = query(
                collection(db, "otherIncome"),
                where("date", ">=", currentStart),
                where("date", "<=", currentEnd)
            );
            const qExpenseCurr = query(
                collection(db, "otherExpenses"),
                where("date", ">=", currentStart),
                where("date", "<=", currentEnd)
            );
            const qDealerPayCurr = query(
                collection(db, "dealerPayments"),
                where("date", ">=", currentStart),
                where("date", "<=", currentEnd)
            );
            const qStaffAdvCurr = query(
                collection(db, "staffadvances"),
                where("date", "==", selectedDate)
            );
            const qPigmiCurr = query(
                collection(db, "pigmi_savings"),
                where("depositDate", "==", selectedDate)
            );
            const qRdCurr = query(
                collection(db, "rd_savings"),
                where("depositDate", "==", selectedDate)
            );

            const [
                paySnap,
                tripSnap,
                otherIncomeSnap,
                expenseSnap,
                dealerPaySnap,
                staffAdvSnap,
                pigmiSnap,
                rdSnap,
            ] = await Promise.all([
                getDocs(qPayCurr),
                getDocs(qTripsCurr),
                getDocs(qOtherIncomeCurr),
                getDocs(qExpenseCurr),
                getDocs(qDealerPayCurr),
                getDocs(qStaffAdvCurr),
                getDocs(qPigmiCurr),
                getDocs(qRdCurr),
            ]);

            const creditList = [];
            const debitList = [];
            const modeMap = {};

            const addPayMode = (mode, amount) => {
                if (!isCashMode(mode)) {
                    const key = mode;
                    modeMap[key] = (modeMap[key] || 0) + Number(amount);
                }
            };

            // Process Customer Payments (Income)
            paySnap.docs.forEach((doc) => {
                const d = doc.data();
                if (!d.tripId) {
                    const amt = Number(d.amount || d.paidAmount || 0);
                    const mode = d.paymentMode || d.payMode || "Cash";
                    const resolvedName =
                        customerMap[d.customerId] ||
                        d.customerName ||
                        d.displayCustomer ||
                        "Customer Payment";

                    creditList.push({
                        id: doc.id,
                        particular: resolvedName,
                        amount: amt,
                        mode: mode,
                        note: d.note || "",
                    });
                    addPayMode(mode, amt);
                }
            });

            // Process Trip Income
            tripSnap.docs.forEach((doc) => {
                const trip = doc.data();
                (trip.payments || []).forEach((p, idx) => {
                    const amt = Number(p.amount || 0);
                    const mode = p.mode || p.paymentMode || "Cash";
                    const resolvedName =
                        customerMap[trip.customerId] ||
                        trip.customerName ||
                        "General";

                    creditList.push({
                        id: `${doc.id}_${idx}`,
                        particular: `${resolvedName} (${trip.vehicleNo || "-"})`,
                        amount: amt,
                        mode: mode,
                        note: p.note || `Ticket: ${trip.ticketNo || "-"}`,
                    });
                    addPayMode(mode, amt);
                });
            });

            // Process Other Income
            otherIncomeSnap.docs.forEach((doc) => {
                const d = doc.data();
                const amt = Number(d.amount || 0);
                const mode = d.paymentMode || d.mode || "Cash";
                const headName = incomeHeadMap[d.incomeHeadId] || d.incomeHeadId || "Other Income";
                
                creditList.push({
                    id: doc.id,
                    particular: `Other Income: ${headName}${d.description ? ` (${d.description})` : ""}`,
                    amount: amt,
                    mode: mode,
                    note: d.description || "",
                });
                addPayMode(mode, amt);
            });

            // Process General Expenses
            expenseSnap.docs.forEach((doc) => {
                const d = doc.data();
                debitList.push({
                    id: doc.id,
                    particular: d.description || d.title || "General Expense",
                    amount: Number(d.amount || 0),
                    category: "Expense",
                });
            });

            // Process Dealer Payments
            dealerPaySnap.docs.forEach((doc) => {
                const d = doc.data();
                debitList.push({
                    id: doc.id,
                    particular: `Dealer: ${dealerMap[d.dealerId] || d.displayDealer || "Dealer"}`,
                    amount: Number(d.amount || 0),
                    category: "Dealer Pay",
                });
            });

            // Process Staff Advance Payments
            staffAdvSnap.docs.forEach((doc) => {
                const d = doc.data();
                const staffName = staffMap[d.staffId] || "Staff";
                const mode = d.payType ? ` (${d.payType})` : "";
                debitList.push({
                    id: doc.id,
                    particular: `Staff Advance: ${staffName}${mode}`,
                    amount: Number(d.payment || 0),
                    category: "Staff Advance",
                });
            });

            // Process Pigmi / Savings Deposits (Debit Side)
            pigmiSnap.docs.forEach((doc) => {
                const d = doc.data();
                const bank = d.bankName ? ` (${d.bankName})` : "";
                debitList.push({
                    id: doc.id,
                    particular: `Pigmi Deposit: ${d.description || "Pigmi Savings"}${bank}`,
                    amount: Number(d.amount || 0),
                    category: "Pigmi Deposit",
                });
            });

            // Process RD Savings Deposits (Debit Side)
            rdSnap.docs.forEach((doc) => {
                const d = doc.data();
                const bank = d.bankName ? ` (${d.bankName})` : "";
                debitList.push({
                    id: doc.id,
                    particular: `RD Deposit: ${d.description || "Recurring Deposit"}${bank}`,
                    amount: Number(d.amount || 0),
                    category: "RD Deposit",
                });
            });

            setCredits(creditList);
            setDebits(debitList);
            setPayModeSummary(modeMap);
            setDataLoaded(true);
        } catch (error) {
            console.error("Error in daily ledger calculation:", error);
            alert("Failed to compute ledger calculations.");
        } finally {
            setLoading(false);
        }
    };

    // Calculation Totals
    const totalCredit = credits.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const totalDebit = debits.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const totalCreditWithOpening = openingBalance + totalCredit;

    // Filter out Non-Cash Payment Modes
    const onlinePaymentModes = Object.entries(payModeSummary).filter(
        ([mode]) => !isCashMode(mode)
    );

    const totalOnlineAmount = onlinePaymentModes.reduce(
        (sum, [_, amt]) => sum + Number(amt || 0),
        0
    );

    // Final Cash-in-Hand Closing Balance calculation
    const closingBalance = totalCreditWithOpening - totalDebit - totalOnlineAmount;

    // Table Row Count Equalizer
    const maxRows = Math.max(credits.length + 1, debits.length, 10);

    return (
        <Box p={2}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Daily Ledger 
            </Typography>

            <Card sx={{ p: 2, mb: 3 }} className="no-print">
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Select Date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button
                            variant="contained"
                            onClick={fetchLedgerData}
                            disabled={loading}
                            sx={{ mr: 1 }}
                        >
                            {loading ? <CircularProgress size={22} /> : "Fetch Data"}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => window.print()}
                            disabled={!dataLoaded}
                        >
                            Print Report
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            {/* Print Sheet View */}
            <Card sx={{ p: 2 }}>
                <Box className="printable-area" p={1}>
                    <Box sx={{ border: "2px solid #000", p: 2, bgcolor: "#fff" }}>
                        {/* Header */}
                        <Box textAlign="center" mb={1}>
                            <Typography variant="h5" fontWeight="bold" sx={{ letterSpacing: 1 }}>
                                Tejas Stone Crusher Uple (M) Osmanabad
                            </Typography>
                            <Box display="flex" justifyContent="flex-end" mt={1}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Date: {selectedDate.split("-").reverse().join("/")}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Split Table Ledger */}
                        <Table
                            size="small"
                            sx={{
                                border: "2px solid #000",
                                "& th, & td": {
                                    border: "1px solid #000",
                                    padding: "5px 8px",
                                    fontSize: "13px",
                                },
                            }}
                        >
                            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                                <TableRow>
                                    <TableCell align="center" width="5%"><strong>Sr</strong></TableCell>
                                    <TableCell width="30%"><strong>Particular (Credit)</strong></TableCell>
                                    <TableCell align="right" width="15%"><strong>Credit Amount</strong></TableCell>
                                    <TableCell width="35%"><strong>Particular (Debit)</strong></TableCell>
                                    <TableCell align="right" width="15%"><strong>Debit Amount</strong></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {/* 1st Row Opening Balance on Credit Side */}
                                <TableRow>
                                    <TableCell align="center">1</TableCell>
                                    <TableCell><strong>मा. शिल्लक (Opening Balance)</strong></TableCell>
                                    <TableCell align="right">
                                        <strong>₹{Number(openingBalance || 0).toLocaleString()}</strong>
                                    </TableCell>
                                    <TableCell>{debits[0]?.particular || ""}</TableCell>
                                    <TableCell align="right">
                                        {debits[0] ? `₹${Number(debits[0].amount || 0).toLocaleString()}` : ""}
                                    </TableCell>
                                </TableRow>

                                {/* Dynamic Rows */}
                                {Array.from({ length: maxRows - 1 }).map((_, index) => {
                                    const creditItem = credits[index];
                                    const debitItem = debits[index + 1];

                                    return (
                                        <TableRow key={index}>
                                            <TableCell align="center">{index + 2}</TableCell>

                                            {/* Credit Particular */}
                                            <TableCell>
                                                {creditItem ? (
                                                    <>
                                                        {creditItem.particular}
                                                        {creditItem.mode && !isCashMode(creditItem.mode) ? ` (${creditItem.mode})` : ""}
                                                    </>
                                                ) : (
                                                    ""
                                                )}
                                            </TableCell>

                                            {/* Credit Amount */}
                                            <TableCell align="right">
                                                {creditItem ? `₹${Number(creditItem.amount || 0).toLocaleString()}` : ""}
                                            </TableCell>

                                            {/* Debit Particular */}
                                            <TableCell>{debitItem ? debitItem.particular : ""}</TableCell>

                                            {/* Debit Amount */}
                                            <TableCell align="right">
                                                {debitItem ? `₹${Number(debitItem.amount || 0).toLocaleString()}` : ""}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                                {/* Non-Cash/Online Payment Modes Deduction Rows */}
                                {onlinePaymentModes.map(([mode, amount], idx) => (
                                    <TableRow key={`online_${idx}`} sx={{ bgcolor: "#fffde7" }}>
                                        <TableCell align="center">-</TableCell>
                                        <TableCell colSpan={2} />
                                        <TableCell>
                                            <strong>Less: Online Payment ({mode})</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>- ₹{Number(amount || 0).toLocaleString()}</strong>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* Totals Row */}
                                <TableRow sx={{ bgcolor: "#eee", fontWeight: "bold" }}>
                                    <TableCell colSpan={2} align="right">
                                        <strong>Total Credit:</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>₹{Number(totalCreditWithOpening || 0).toLocaleString()}</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>Total Debit + Online:</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>₹{Number((totalDebit + totalOnlineAmount) || 0).toLocaleString()}</strong>
                                    </TableCell>
                                </TableRow>

                                {/* Closing Balance Row */}
                                <TableRow sx={{ bgcolor: "#e8f5e9" }}>
                                    <TableCell colSpan={3} />
                                    <TableCell align="right">
                                        <strong>Closing Balance (Cash):</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>₹{Number(closingBalance || 0).toLocaleString()}</strong>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
            </Card>

            {/* Print Styles */}
            <style>{`
                @media print {
                  body * {
                    visibility: hidden;
                  }
                  .printable-area, .printable-area * {
                    visibility: visible;
                  }
                  .printable-area {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                  }
                  .no-print {
                    display: none !important;
                  }
                  @page {
                    size: A4 portrait;
                    margin: 10mm;
                  }
                }
            `}</style>
        </Box>
    );
}

export default Dailyledger;